import { ActionButton, DialogTrigger, Flex, Link, ProgressCircle, View } from '@adobe/react-spectrum';
import { useBreakpoint } from '@react-spectrum/utils';
import Rating from '@react/react-spectrum/Rating';
import { error } from '@react/react-spectrum/Toast';
import Checkmark from '@spectrum-icons/workflow/Checkmark';
import ChevronDown from '@spectrum-icons/workflow/ChevronDown';
import ChevronUp from '@spectrum-icons/workflow/ChevronUp';
import Close from '@spectrum-icons/workflow/Close';
// eslint-disable-next-line import/no-named-as-default
import produce from 'immer';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';

import { excApi, handleApiCall } from '@exchange-frontends/api';
import { Case, ErrorBoundary, SwitchContainer, WithSkeleton, WithTimeout } from '@exchange-frontends/components';
import { useIsMounted } from '@exchange-frontends/custom-hooks';

import { useStore } from '../../../store';
import { dateFormatter } from '../../../utils/dateFormatter';
import { UserDataContext } from '../../App';
import { LeaveReview } from './LeaveReview';
import { Label } from './RatingsAndReviews';
import { Reply } from './Reply';
import { ReplyDialog } from './ReplyDialog';

const PAGE_SIZE = 10;

export const Review = (props) => {
  const { review, belongsToCurrentUser, setDeleteSuccess, isLoading } = props;

  const { matchedBreakpoints } = useBreakpoint();
  const isMobileSized = !matchedBreakpoints.includes('L');
  const { profile, accessToken } = useContext(UserDataContext) ?? {};
  const isMounted = useIsMounted();
  const acquired = useStore((state) => state.acquired);
  const isUserPartOfOrg = useStore((state) => state.isUserPartOfOrg);
  const {
    reviewTitle: title,
    reviewText: text,
    authorName,
    replyCount: initialReplyCount = 0,
    lastEditedAt,
    createdAt,
    assetMetadata,
    rating,
    id,
  } = review ?? {};

  const [areRepliesOpen, setAreRepliesOpen] = useState(false);
  const [deletePressed, setDeletePressed] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [shouldShowUpdateMessage, setShouldShowUpdateMessage] = useState(false);
  const [replyAddedStatusMessage, setReplyAddedStatusMessage] = useState(null);
  const [replyDeleted, setReplyDeleted] = useState(false);

  const [replyCount, setReplyCount] = useState(initialReplyCount);
  const [repliesOffset, setRepliesOffset] = useState(0);
  const [replies, setReplies] = useState([]);
  const [repliesFetchError, setRepliesFetchError] = useState(null);
  const totalPages = Math.ceil(replyCount / PAGE_SIZE);
  const currentPage = repliesOffset / PAGE_SIZE + 1;
  const shouldShowLoadMoreButton = currentPage < totalPages;
  const canAddReply = ((accessToken && acquired) || isUserPartOfOrg) && replyCount < 50;
  const RepliesIcon = areRepliesOpen ? ChevronUp : ChevronDown;

  const handleAddReply = async (reviewId, text) => {
    try {
      const handler = handleApiCall(excApi.addReply, {
        accessToken,
        reviewId,
        authorGuid: profile?.userId,
        authorName: profile?.first_name + ' ' + profile?.last_name,
        text,
      });
      const response = await handler.run();
      setReplyCount((prev) => prev + 1);
      if (replyDeleted) {
        setReplyDeleted(false);
      }
      setReplyAddedStatusMessage({ type: 'positive', message: 'Your reply has been added.' });
      if (currentPage === totalPages && replies.length !== 0) {
        // last page, add reply in the array
        setReplies(
          produce((draft) => {
            draft.push({ ...response, isEditable: true });
          })
        );
      }
    } catch (err) {
      console.error(err);
      if (isMounted.current) {
        setReplyAddedStatusMessage({
          type: 'negative',
          message: err.userMessage,
        });
      }
    }
  };

  const handleRepliesFetching = async (offset = 0) => {
    const handler = handleApiCall(excApi.getReplies, {
      reviewId: id,
      offset: offset,
      limit: PAGE_SIZE,
      userId: profile?.userId,
      accessToken,
    });
    setRepliesOffset(offset);
    const page = offset / PAGE_SIZE + 1;
    const loadingElementsLength = page < totalPages ? PAGE_SIZE : replyCount - (totalPages - 1) * PAGE_SIZE;
    const loadingElements = Array(loadingElementsLength).fill({ loading: true });
    try {
      setReplies((state) => [...state, ...loadingElements]);
      const response = await handler.run();
      if (isMounted.current) {
        setReplies((state) => [...state.filter(({ loading }) => !loading), ...response.items]);
      }
    } catch (err) {
      console.error(err);
      if (isMounted.current) {
        setReplies((state) => [...state.filter(({ loading }) => !loading)]);
        setRepliesFetchError(err.userMessage);
      }
    }
  };

  const onReplyUpdated = (newReply) => {
    setReplies(
      produce((draft) => {
        const reply = draft.find((reply) => reply.id === newReply.id);
        reply.text = newReply.text;
      })
    );
  };

  const onReplyDeleted = (id) => {
    if (replyCount === 1) {
      setAreRepliesOpen(false);
    }
    setReplies(
      produce((draft) => {
        const index = draft.findIndex((reply) => reply.id === id);
        if (index !== -1) draft.splice(index, 1);
      })
    );
    setReplyCount((prev) => prev - 1);
    if (replyAddedStatusMessage) {
      setReplyAddedStatusMessage(null);
    }
    setReplyDeleted(true);
  };

  const handleDeleteReview = async () => {
    try {
      setDeleteLoading(true);
      const handler = handleApiCall(excApi.deleteReview, { accessToken, imsUserId: profile.userId, reviewId: id });
      await handler.run();
      useStore.setState({ currentUserReview: null });
      setDeleteSuccess(true);
    } catch (err) {
      console.error(err);
      error(err.userMessage, { timeout: 0 });
    } finally {
      if (isMounted.current) {
        setDeletePressed(false);
        setDeleteLoading(false);
      }
    }
  };

  const toggleShouldShowUpdateMessage = () => {
    setShouldShowUpdateMessage((state) => !state);
  };

  return (
    <Flex direction="column" gap="size-100">
      {!belongsToCurrentUser && <Rating value={rating} readOnly disabled={isLoading} />}
      <WithSkeleton shouldRender={!isLoading} width={isMobileSized ? '80%' : '60%'}>
        <em>
          <Label>
            By {authorName} -{' '}
            {lastEditedAt || createdAt ? dateFormatter.format(new Date(lastEditedAt ?? createdAt)) : ''} (Version{' '}
            {assetMetadata?.version})
          </Label>
        </em>
      </WithSkeleton>
      <WithSkeleton shouldRender={!isLoading} width={isMobileSized ? '60%' : '40%'}>
        <strong style={{ overflowWrap: 'anywhere' }}>{title}</strong>
      </WithSkeleton>
      <WithSkeleton count={3} shouldRender={!isLoading}>
        <div style={{ overflowWrap: 'anywhere' }}>
          {text
            ?.split('\n')
            .filter((paragraph) => paragraph.length > 0)
            .map((paragraph, idx) => (
              <p style={{ margin: '0' }} key={`${id}-p-${idx}`}>
                {paragraph}
              </p>
            ))}
        </div>
      </WithSkeleton>

      <Flex width="100%" wrap="wrap" gap="size-100" alignItems="center">
        <Flex gap="size-200" alignItems="center" maxHeight="21px">
          {belongsToCurrentUser && !isLoading && (
            <DialogTrigger>
              <ActionButton isQuiet isDisabled={deletePressed} maxHeight="size-300" marginStart="-4px">
                Edit
              </ActionButton>
              {(close) => <LeaveReview close={close} onSuccessfulReview={toggleShouldShowUpdateMessage} />}
            </DialogTrigger>
          )}
          {belongsToCurrentUser && !isLoading && (
            <ActionButton
              isQuiet
              maxHeight="size-300"
              isDisabled={deletePressed}
              onPress={() => {
                setDeletePressed(true);
              }}
            >
              Delete
            </ActionButton>
          )}
          {(canAddReply || isLoading) && (
            <WithSkeleton shouldRender={!isLoading} width={24} height={24}>
              <DialogTrigger>
                <ActionButton isQuiet maxHeight="size-300">
                  Reply
                </ActionButton>
                {(close) => <ReplyDialog close={close} variant="add" reviewId={id} addReply={handleAddReply} />}
              </DialogTrigger>
            </WithSkeleton>
          )}
          {(replyCount > 0 || isLoading) && (
            <WithSkeleton shouldRender={!isLoading} width={60} height={24}>
              <ActionButton
                isQuiet
                maxHeight="size-300"
                onPress={() => {
                  const shouldShowReplies = !areRepliesOpen;
                  setAreRepliesOpen(shouldShowReplies);
                  // don't refetch the first page if you keep toggling view replies
                  if (shouldShowReplies && replies.length === 0) {
                    handleRepliesFetching();
                  }
                }}
                aria-expanded={areRepliesOpen}
                aria-controls="replies-list"
              >
                <RepliesIcon />
                <span style={{ paddingInline: '7px' }}>Replies ({replyCount})</span>
              </ActionButton>
            </WithSkeleton>
          )}
        </Flex>
        <WithTimeout
          setShouldShow={() => setReplyAddedStatusMessage(null)}
          shouldShow={replyAddedStatusMessage !== null}
        >
          <Flex alignItems="center" gap="size-100">
            <SwitchContainer>
              <Case when={replyAddedStatusMessage?.type === 'positive'}>
                <Checkmark color="positive" width="18px" />
              </Case>
              <Case when={replyAddedStatusMessage?.type === 'negative'}>
                <Close color="positive" width="18px" />
              </Case>
            </SwitchContainer>
            <Label>{replyAddedStatusMessage?.message}</Label>
          </Flex>
        </WithTimeout>
        <WithTimeout setShouldShow={setReplyDeleted} shouldShow={replyDeleted}>
          <Flex alignItems="center" gap="size-100">
            <Checkmark color="positive" width="18px" />
            <Label>Your reply has been deleted.</Label>
          </Flex>
        </WithTimeout>
      </Flex>

      <WithTimeout shouldShow={shouldShowUpdateMessage} setShouldShow={toggleShouldShowUpdateMessage}>
        <Flex alignItems="center" gap="size-100" marginY="size-100">
          <Checkmark color="positive" />
          <Label>Your review has been updated</Label>
        </Flex>
      </WithTimeout>
      {deletePressed && !isLoading && (
        <Flex alignItems="center" gap="size-100">
          <Label>Are you sure you want to delete your review? </Label>
          <ActionButton onPress={handleDeleteReview} isDisabled={deleteLoading}>
            <Flex marginX="size-50">
              {deleteLoading && (
                <ProgressCircle size="S" isIndeterminate marginEnd="size-100" aria-label="Deleting your review" />
              )}
              <Label>Yes, delete</Label>
            </Flex>
          </ActionButton>
          <ActionButton
            isDisabled={deleteLoading}
            onPress={() => {
              setDeletePressed(false);
            }}
          >
            <View paddingX="size-50">
              <Label>Cancel</Label>
            </View>
          </ActionButton>
        </Flex>
      )}
      {areRepliesOpen && (
        <Flex direction="column" gap="size-300" marginTop="size-200" id="replies-list">
          {replies.map((reply, index) => (
            <Reply
              key={reply?.id ?? index}
              reviewId={id}
              reply={reply}
              onDeleteSuccess={onReplyDeleted}
              onUpdateSuccess={onReplyUpdated}
            />
          ))}
          <ErrorBoundary errorMessage={repliesFetchError} isError={repliesFetchError !== null}>
            <>
              {shouldShowLoadMoreButton && (
                <Link
                  onPress={() => {
                    handleRepliesFetching(repliesOffset + PAGE_SIZE);
                  }}
                >
                  Load more replies
                </Link>
              )}
            </>
          </ErrorBoundary>
        </Flex>
      )}
    </Flex>
  );
};

Review.propTypes = {
  review: PropTypes.object,
  belongsToCurrentUser: PropTypes.bool,
  setDeleteSuccess: PropTypes.func,
  isLoading: PropTypes.bool,
};
